/**
 * Capabilities — what this organization delivers, and where.
 *
 * The finder only matches on CONFIRMED capabilities, so this is the review
 * step: the platform proposes items from the org's programs and billing codes,
 * and a person confirms, rejects or corrects each one. The profile (states,
 * counties, NPIs, languages) decides which funders are in reach at all.
 */
import React, { useState } from 'react';
import { Check, Plus, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SectionHeader, PageState } from '@/components/weavein';
import type { CapabilityItem, CapabilityKind, CapabilityProfile, FundingApi } from '@/services/api/fundingOpportunities';
import { CARD, MUTED, ErrorNote, Pill, STATUS_LABEL, fromList, toList, useLoad } from './shared';

const PROFILE_FIELDS: { key: keyof CapabilityProfile; label: string; hint: string }[] = [
  { key: 'service_states', label: 'States you serve', hint: 'Two-letter codes, comma separated — e.g. CA, NV' },
  { key: 'service_counties', label: 'Counties', hint: 'Optional, comma separated' },
  { key: 'npis', label: 'NPIs', hint: 'Organization and rendering NPIs funders will see' },
  { key: 'languages', label: 'Languages', hint: 'Languages your staff deliver services in' },
  { key: 'provider_types', label: 'Provider types', hint: 'e.g. CBO, CHW program, doula group, FQHC' },
];

type ListKey = 'service_states' | 'service_counties' | 'npis' | 'languages' | 'provider_types';

const ProfileCard: React.FC<{ api: FundingApi; onSaved: () => void }> = ({ api, onSaved }) => {
  const { data, error, loading, setData } = useLoad(() => api.getProfile(), [api]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const value = (k: ListKey) => (k in draft ? draft[k] : fromList(data?.[k]));

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const patch: Partial<CapabilityProfile> = {};
      (Object.keys(draft) as ListKey[]).forEach((k) => {
        patch[k] = k === 'service_states' ? toList(draft[k]).map((s) => s.toUpperCase()) : toList(draft[k]);
      });
      setData(await api.saveProfile(patch));
      setDraft({});
      onSaved();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) return <PageState variant="loading" />;
  return (
    <div className={`${CARD} space-y-4 p-4`}>
      <SectionHeader
        title="Where you work"
        description="Funders are matched only in the places you serve."
        action={
          <Button size="sm" onClick={save} disabled={saving || Object.keys(draft).length === 0}>
            {saving ? 'Saving…' : 'Save profile'}
          </Button>
        }
      />
      {error && <ErrorNote message={error} />}
      {saveError && <ErrorNote message={saveError} />}
      <div className="grid gap-3 md:grid-cols-2">
        {PROFILE_FIELDS.map((f) => (
          <label key={f.key} className="block space-y-1">
            <span className="text-[13px] font-medium text-ink">{f.label}</span>
            <Input
              value={value(f.key as ListKey)}
              onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
              placeholder={f.hint}
            />
            <span className="block text-[12px] text-ink-400">{f.hint}</span>
          </label>
        ))}
      </div>
      {data?.reviewed_at && <p className={MUTED}>Last reviewed {new Date(data.reviewed_at).toLocaleDateString()}.</p>}
    </div>
  );
};

const ItemRow: React.FC<{ item: CapabilityItem; onChange: (patch: Partial<CapabilityItem>) => Promise<void> }> = ({ item, onChange }) => {
  const [busy, setBusy] = useState(false);
  const act = async (patch: Partial<CapabilityItem>) => {
    setBusy(true);
    try {
      await onChange(patch);
    } finally {
      setBusy(false);
    }
  };
  const reasons = item.evidence?.reasons ?? [];
  return (
    <li className="flex flex-col gap-2 border-b border-v2border-muted py-3 last:border-0 md:flex-row md:items-start md:justify-between">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[14px] font-medium text-ink">{item.label}</span>
          <Pill tone={item.status === 'confirmed' ? 'success' : item.status === 'rejected' ? 'neutral' : 'attention'}>
            {STATUS_LABEL[item.status]}
          </Pill>
          <Pill>{item.kind === 'program' ? 'Program' : 'Service'}</Pill>
        </div>
        {(item.service_codes.length > 0 || item.program_types.length > 0) && (
          <p className={MUTED}>
            {[...item.service_codes, ...item.program_types].join(' · ')}
          </p>
        )}
        {reasons.length > 0 && <p className="text-[12px] text-ink-400">{reasons.join(' ')}</p>}
      </div>
      <div className="flex shrink-0 gap-2">
        {item.status !== 'confirmed' && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => act({ status: 'confirmed' })}>
            <Check className="mr-1 h-3.5 w-3.5" /> Confirm
          </Button>
        )}
        {item.status !== 'rejected' && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => act({ status: 'rejected' })}>
            <X className="mr-1 h-3.5 w-3.5" /> Not us
          </Button>
        )}
      </div>
    </li>
  );
};

const CapabilitiesPanel: React.FC<{ api: FundingApi; onChanged: () => void }> = ({ api, onChanged }) => {
  const { data: items, error, loading, reload, setData } = useLoad(() => api.listCapabilities(), [api]);
  const [deriving, setDeriving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [newCodes, setNewCodes] = useState('');
  const [newKind, setNewKind] = useState<CapabilityKind>('provider_service');

  const derive = async () => {
    setDeriving(true);
    setActionError(null);
    try {
      await api.deriveCapabilities();
      await reload();
      onChanged();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeriving(false);
    }
  };

  const update = async (id: number, patch: Partial<CapabilityItem>) => {
    setActionError(null);
    try {
      const next = await api.updateCapability(id, patch);
      setData((cur) => (cur ?? []).map((i) => (i.id === id ? next : i)));
      onChanged();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
  };

  const add = async () => {
    if (!newLabel.trim()) return;
    setActionError(null);
    try {
      const created = await api.addCapability({
        kind: newKind,
        label: newLabel.trim(),
        service_codes: toList(newCodes).map((c) => c.toUpperCase()),
        program_types: [],
      });
      setData((cur) => [...(cur ?? []), created]);
      setNewLabel('');
      setNewCodes('');
      onChanged();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
  };

  const proposed = (items ?? []).filter((i) => i.status === 'proposed');
  const confirmed = (items ?? []).filter((i) => i.status === 'confirmed');
  const rejected = (items ?? []).filter((i) => i.status === 'rejected');

  return (
    <div className="space-y-4">
      <ProfileCard api={api} onSaved={onChanged} />

      <div className={`${CARD} space-y-3 p-4`}>
        <SectionHeader
          title="Provider and program capabilities"
          description="What you deliver. Only confirmed items are matched against funders."
          action={
            <Button size="sm" variant="outline" onClick={derive} disabled={deriving}>
              <RefreshCw className={`mr-1 h-3.5 w-3.5 ${deriving ? 'animate-spin' : ''}`} />
              {deriving ? 'Reading your programs…' : 'Re-derive from my programs'}
            </Button>
          }
        />
        {error && <ErrorNote message={error} />}
        {actionError && <ErrorNote message={actionError} />}
        {loading && !items && <PageState variant="loading" />}
        {items && items.length === 0 && (
          <PageState
            variant="empty"
            title="No capabilities yet"
            message="Read them from your programs and billing codes, then confirm what is right."
            action={<Button size="sm" onClick={derive} disabled={deriving}>Derive from my programs</Button>}
          />
        )}
        {proposed.length > 0 && (
          <div>
            <p className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-ink-500">To review ({proposed.length})</p>
            <ul>{proposed.map((i) => <ItemRow key={i.id} item={i} onChange={(p) => update(i.id, p)} />)}</ul>
          </div>
        )}
        {confirmed.length > 0 && (
          <div>
            <p className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-ink-500">Confirmed ({confirmed.length})</p>
            <ul>{confirmed.map((i) => <ItemRow key={i.id} item={i} onChange={(p) => update(i.id, p)} />)}</ul>
          </div>
        )}
        {rejected.length > 0 && (
          <details>
            <summary className={`${MUTED} cursor-pointer`}>Marked not ours ({rejected.length})</summary>
            <ul>{rejected.map((i) => <ItemRow key={i.id} item={i} onChange={(p) => update(i.id, p)} />)}</ul>
          </details>
        )}

        <div className="grid gap-2 border-t border-v2border-muted pt-3 md:grid-cols-[140px_1fr_1fr_auto]">
          <select
            value={newKind}
            onChange={(e) => setNewKind(e.target.value as CapabilityKind)}
            className="h-9 rounded-md border border-v2border bg-surface px-2 text-[13px] text-ink"
            aria-label="Capability type"
          >
            <option value="provider_service">Service</option>
            <option value="program">Program</option>
          </select>
          <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="e.g. Doula prenatal visits" aria-label="Capability name" />
          <Input value={newCodes} onChange={(e) => setNewCodes(e.target.value)} placeholder="Service codes, e.g. T1032, 59409" aria-label="Service codes" />
          <Button size="sm" onClick={add} disabled={!newLabel.trim()}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Add
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CapabilitiesPanel;
